import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import PlaybookList from '@/components/playbooks/PlaybookList';
import { useApp } from '@/contexts/AppContext';
import { buildAdultingAssistantResponse, type AssistantGuideRecord } from '../../ui-foundation/content';
import {
  AppButton,
  AppCard,
  AppEmptyState,
  AppFieldGroup,
  AppInput,
  AppPage,
  AppPanelButton,
  AppSectionHeader,
  AppSectionHero,
  AppTabBar,
  AppTabButton,
  AppText,
  AppTextArea,
} from '../../ui-foundation';
import {
  GUIDE_HUB_META,
  GUIDE_SURFACE_META,
  GUIDE_SURFACE_ORDER,
  type GuideSurfaceSection,
} from '../../ui-foundation/features/guides';
import { filterGuideItems, splitGuideItems, useGuideGroups } from '@/components/playbooks/guideData';

function parseGuideSection(value: string | null): GuideSurfaceSection | null {
  if (!value) return null;
  return GUIDE_SURFACE_ORDER.includes(value as GuideSurfaceSection) ? (value as GuideSurfaceSection) : null;
}

export default function GuidesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data } = useApp();
  const selectedSection = parseGuideSection(searchParams.get('section'));
  const { groups, guides, loading, error } = useGuideGroups(selectedSection);
  const [assistantQuery, setAssistantQuery] = useState('');
  const [supportQuery, setSupportQuery] = useState('');
  const [assistantDraft, setAssistantDraft] = useState('');
  const [assistantHeadline, setAssistantHeadline] = useState<string | null>(null);
  const [assistantSummary, setAssistantSummary] = useState<string | null>(null);
  const [assistantSteps, setAssistantSteps] = useState<string[]>([]);
  const [assistantGuides, setAssistantGuides] = useState<
    Array<{ id: string; kind: 'playbook' | 'content-card'; section: GuideSurfaceSection; title: string }>
  >([]);

  const assistantGuideRecords = useMemo<AssistantGuideRecord[]>(
    () =>
      guides.map((guide) => ({
        id: guide.id,
        kind: guide.kind,
        section: guide.section,
        title: guide.title,
        summary: guide.kind === 'playbook' ? guide.description : guide.summary,
        body: guide.kind === 'playbook' ? guide.description : guide.body,
        detail: guide.kind === 'content-card' ? guide.detail : null,
        steps:
          guide.kind === 'playbook'
            ? guide.steps.map((step) => ({
                step: step.step,
                title: step.title,
                description: step.description,
                actions: step.actions,
                callScript: step.call_script,
                tip: step.tip,
              }))
            : [],
      })),
    [guides],
  );

  const visibleSections = useMemo(
    () =>
      selectedSection
        ? [selectedSection]
        : GUIDE_SURFACE_ORDER.filter((section) => (groups.get(section) || []).length > 0),
    [groups, selectedSection],
  );
  const filteredGroups = useMemo(
    () =>
      new Map(
        visibleSections.map((section) => [section, filterGuideItems(groups.get(section) || [], supportQuery)]),
      ),
    [groups, supportQuery, visibleSections],
  );
  const matchingSections = useMemo(
    () => visibleSections.filter((section) => (filteredGroups.get(section) || []).length > 0),
    [filteredGroups, visibleSections],
  );
  const topLevelHelperPacks = useMemo(
    () =>
      matchingSections.flatMap((section) =>
        splitGuideItems(filteredGroups.get(section) || []).helperPacks.map((guide) => ({
          section,
          guide,
        })),
      ),
    [filteredGroups, matchingSections],
  );
  const displayedHubSections = useMemo(
    () => (supportQuery.trim().length ? matchingSections : GUIDE_SURFACE_ORDER),
    [matchingSections, supportQuery],
  );
  const selectedSectionItems = selectedSection ? filteredGroups.get(selectedSection) || [] : [];
  const hasFilteredGuides = useMemo(
    () => matchingSections.length > 0,
    [matchingSections],
  );

  const runAssistant = () => {
    const response = buildAdultingAssistantResponse({
      query: assistantQuery,
      guides: assistantGuideRecords,
      context: {
        firstName: data.profile.firstName,
        partnerName: data.profile.partnerName,
        postcode: data.profile.postcode,
        children: data.profile.children,
      },
    });

    if (!response) {
      toast.error('Write a question or situation first.');
      return;
    }

    setAssistantHeadline(response.headline);
    setAssistantSummary(response.summary);
    setAssistantSteps(response.nextSteps);
    setAssistantGuides(response.relevantGuides);
    setAssistantDraft(response.draft?.body || '');
  };

  const clearAssistant = () => {
    setAssistantQuery('');
    setAssistantDraft('');
    setAssistantHeadline(null);
    setAssistantSummary(null);
    setAssistantSteps([]);
    setAssistantGuides([]);
  };

  const copyDraft = async () => {
    if (!assistantDraft.trim()) return;
    await navigator.clipboard.writeText(assistantDraft);
    toast.success('Draft copied to clipboard.');
  };

  const downloadDraft = () => {
    if (!assistantDraft.trim()) return;

    const blob = new Blob([assistantDraft], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'adulting-draft.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Draft downloaded.');
  };

  const handleSectionChange = (section: GuideSurfaceSection | null) => {
    const nextParams = new URLSearchParams(searchParams);
    if (section) {
      nextParams.set('section', section);
    } else {
      nextParams.delete('section');
    }
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <AppPage className="animate-fade-in">
      <AppSectionHero
        title={selectedSection ? GUIDE_SURFACE_META[selectedSection].hubTitle : 'Support Hub'}
        subtitle={selectedSection ? GUIDE_SURFACE_META[selectedSection].supportAreaDescription : GUIDE_HUB_META.hubSubtitle}
      />

      <AppTabBar flexWrap="wrap">
        <AppTabButton active={!selectedSection} onPress={() => handleSectionChange(null)}>
          All
        </AppTabButton>
        {GUIDE_SURFACE_ORDER.map((section) => (
          <AppTabButton
            key={section}
            active={selectedSection === section}
            onPress={() => handleSectionChange(section)}
          >
            {GUIDE_SURFACE_META[section].label}
          </AppTabButton>
        ))}
      </AppTabBar>

      <AppCard gap="$md">
        <AppFieldGroup label="Search support">
          <AppInput
            value={supportQuery}
            onChange={(event) => setSupportQuery((event.target as HTMLInputElement).value)}
            placeholder="Search guides, support cards, or topics"
          />
        </AppFieldGroup>
        <AppSectionHeader
          title="Ask Adulting"
          subtitle="Plain-English help using the guides and draft patterns already in the app. Ask a question or ask for a draft."
        />
        <AppFieldGroup label="What do you need help with?">
          <AppTextArea
            value={assistantQuery}
            onChange={(event) => setAssistantQuery((event.target as HTMLTextAreaElement).value)}
            rows={3}
            placeholder="Examples: Am I entitled to Free School Meals? Draft me a letter about a broken boiler. What should I do first after redundancy?"
          />
        </AppFieldGroup>
        <div className="flex flex-wrap gap-2">
          <AppButton onPress={runAssistant}>Ask Adulting</AppButton>
          {(assistantHeadline || assistantQuery) ? (
            <AppButton tone="outline" onPress={clearAssistant}>
              Clear
            </AppButton>
          ) : null}
        </div>

        {assistantHeadline ? (
          <div className="space-y-4 rounded-2xl border border-border bg-background px-4 py-4">
            <div className="space-y-1">
              <AppText fontSize="$sm" fontWeight="700">
                {assistantHeadline}
              </AppText>
              {assistantSummary ? (
                <AppText fontSize="$sm" color="$colorMuted">
                  {assistantSummary}
                </AppText>
              ) : null}
            </div>

            {assistantSteps.length ? (
              <div className="space-y-2">
                {assistantSteps.map((step) => (
                  <div key={step} className="flex gap-2">
                    <span className="pt-0.5 text-muted-foreground">•</span>
                    <AppText fontSize="$sm">{step}</AppText>
                  </div>
                ))}
              </div>
            ) : null}

            {assistantGuides.length ? (
              <div className="flex flex-wrap gap-2">
                {assistantGuides.map((guide) => (
                  <AppButton
                    key={`${guide.kind}-${guide.id}`}
                    size="sm"
                    tone="outline"
                    onPress={() => navigate(`/guides/${guide.section}/${guide.kind}/${guide.id}`)}
                  >
                    {guide.title}
                  </AppButton>
                ))}
              </div>
            ) : null}

            {assistantDraft ? (
              <div className="space-y-3 rounded-2xl border border-border bg-card px-4 py-4">
                <AppFieldGroup label="Draft to review and edit">
                  <AppTextArea
                    value={assistantDraft}
                    onChange={(event) => setAssistantDraft((event.target as HTMLTextAreaElement).value)}
                    rows={12}
                  />
                </AppFieldGroup>
                <div className="flex flex-wrap gap-2">
                  <AppButton onPress={() => void copyDraft()}>Copy draft</AppButton>
                  <AppButton tone="outline" onPress={downloadDraft}>
                    Download draft
                  </AppButton>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </AppCard>

      {loading ? (
        <AppSectionHeader title="Loading support hub" subtitle="Pulling the latest guides and support cards." />
      ) : null}

      {!loading && error ? (
        <AppEmptyState title="Support Hub is unavailable right now" body={error} />
      ) : null}

      {!loading && !guides.length && !error ? (
        <AppEmptyState
          title="Support Hub is still being filled"
          body="Active guides and support cards will show here when they are live for this account."
        />
      ) : null}

      {!loading && guides.length > 0 && !selectedSection ? (
        <div className="space-y-6">
          <section className="space-y-3">
            <AppSectionHeader
              title={GUIDE_HUB_META.helperPacksTitle}
              subtitle={GUIDE_HUB_META.helperPacksSubtitle}
            />
            {topLevelHelperPacks.length ? (
              <div className="overflow-x-auto pb-1">
                <div className="flex min-w-max gap-3 pr-1">
                  {topLevelHelperPacks.map(({ section, guide }) => (
                    <AppPanelButton
                      key={`${section}-${guide.kind}-${guide.id}`}
                      onClick={() => navigate(`/guides/${section}/${guide.kind}/${guide.id}`)}
                      className="w-[216px] shrink-0 rounded-2xl border-0 bg-transparent p-0 shadow-none hover:bg-transparent"
                    >
                      <AppCard backgroundColor="$primary" borderColor="$primary" minHeight={104} className="h-full">
                        <div className="flex h-full flex-col justify-between gap-3 px-4 py-4 text-left">
                          <div className="space-y-3">
                            <div className="flex items-start gap-3">
                              <AppText color="$primaryForeground" fontSize={26} lineHeight={28}>
                                {guide.icon || GUIDE_SURFACE_META[section].emoji}
                              </AppText>
                              <AppText flex={1} color="$primaryForeground" fontSize="$sm" lineHeight="$sm" fontWeight="700">
                                {guide.title}
                              </AppText>
                            </div>
                            <AppText color="$primaryForeground" opacity={0.82} fontSize="$sm" lineHeight={18}>
                              {guide.description}
                            </AppText>
                          </div>
                          <AppText color="$primaryForeground" opacity={0.68} fontSize="$xs" lineHeight={16} fontWeight="600">
                            {GUIDE_SURFACE_META[section].label}
                          </AppText>
                        </div>
                      </AppCard>
                    </AppPanelButton>
                  ))}
                </div>
              </div>
            ) : supportQuery.trim().length ? (
              <AppEmptyState
                title="No helper packs matched"
                body="Try a broader term or browse the support areas below."
              />
            ) : null}
          </section>

          <section className="space-y-3">
            <AppSectionHeader
              title={GUIDE_HUB_META.supportAreasTitle}
              subtitle={GUIDE_HUB_META.supportAreasSubtitle}
            />
            {displayedHubSections.length ? (
              <div className="grid gap-3 sm:grid-cols-2">
                {displayedHubSections.map((section) => (
                  <AppPanelButton
                    key={section}
                    onClick={() => handleSectionChange(section)}
                    className="rounded-2xl border-0 bg-transparent p-0 shadow-none hover:bg-transparent"
                  >
                    <AppCard className="h-full">
                      <div className="space-y-2 px-4 py-4 text-center">
                        <AppText fontSize="$hero" lineHeight="$hero" textAlign="center">
                          {GUIDE_SURFACE_META[section].emoji}
                        </AppText>
                        <AppText fontSize="$sm" lineHeight="$sm" fontWeight="700" textAlign="center">
                          {GUIDE_SURFACE_META[section].label}
                        </AppText>
                        <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm" textAlign="center">
                          {GUIDE_SURFACE_META[section].supportAreaDescription}
                        </AppText>
                      </div>
                    </AppCard>
                  </AppPanelButton>
                ))}
              </div>
            ) : supportQuery.trim().length ? (
              <AppEmptyState
                title="No support areas matched"
                body="Try a broader term or browse the helper packs above."
              />
            ) : null}
          </section>
        </div>
      ) : null}

      {!loading && selectedSection ? (
        <div className="space-y-4">
          <AppCard gap="$sm">
            <AppText
              color="$primary"
              fontSize="$xs"
              lineHeight="$sm"
              fontWeight="700"
              textTransform="uppercase"
              letterSpacing={1.2}
            >
              Support area
            </AppText>
            <div className="flex items-start gap-3">
              <AppText fontSize="$hero" lineHeight="$hero">
                {GUIDE_SURFACE_META[selectedSection].emoji}
              </AppText>
              <div className="min-w-0 flex-1 space-y-1">
                <AppText fontSize="$lg" lineHeight="$lg" fontWeight="700">
                  {GUIDE_SURFACE_META[selectedSection].label}
                </AppText>
                <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                  {GUIDE_SURFACE_META[selectedSection].guidesSubtitle}
                </AppText>
              </div>
            </div>
          </AppCard>

          <PlaybookList
            section={selectedSection}
            guides={selectedSectionItems}
            loading={loading}
            emptyTitle={`No ${GUIDE_SURFACE_META[selectedSection].label.toLowerCase()} support is live yet`}
            emptyBody="Active guides and support cards will show here when they are live for this section."
            showSectionHeaders
            helperPacksSubtitle={GUIDE_SURFACE_META[selectedSection].helperPackSubtitle}
            supportCardsSubtitle={GUIDE_HUB_META.supportCardsSubtitle}
          />
        </div>
      ) : null}
    </AppPage>
  );
}
